import {
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  /** When true, Escape and backdrop do not dismiss. */
  dismissible?: boolean;
  children: ReactNode;
  className?: string;
  /** Applied to the centred panel (not the backdrop). */
  panelClassName?: string;
  /** Applied to the full-viewport dismiss/scrim layer. */
  backdropClassName?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
}

/**
 * Full-viewport overlay with a centred panel. Backdrop + Escape dismiss when
 * `dismissible` (default true).
 */
export function Modal({
  open,
  onClose,
  dismissible = true,
  children,
  className,
  panelClassName,
  backdropClassName,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    // Move focus into the panel for keyboard users.
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, dismissible, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        className,
      )}
      role="presentation"
    >
      <button
        type="button"
        aria-label={dismissible ? 'Close' : undefined}
        aria-hidden={!dismissible}
        tabIndex={dismissible ? 0 : -1}
        className={cn(
          'absolute inset-0 bg-bg-overlay',
          !dismissible && 'cursor-default',
          backdropClassName,
        )}
        onClick={() => {
          if (dismissible) onClose?.();
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy ?? titleId}
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cn(
          'relative z-10 outline-none',
          panelClassName,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
